import React, { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * Sizes the word so its ink exactly spans the container's content box, giving
 * equal left and right margins at every viewport width. Re-fits when the web
 * font finishes loading and whenever the container resizes.
 */
/** Optical kerning (in em) for pairs whose serifs touch at this size; every other pair is already clear. */
const KERN = { AD: 0.05, RP: 0.05 };

const FitWord = ({ text, className }) => {
  const box = useRef(null);
  const word = useRef(null);

  const fit = useCallback(() => {
    const b = box.current;
    const w = word.current;
    if (!b || !w) return;
    const cs = getComputedStyle(b);
    const avail =
      b.clientWidth - parseFloat(cs.paddingLeft) - parseFloat(cs.paddingRight);
    w.style.fontSize = "100px";
    const natural = w.getBoundingClientRect().width;
    if (natural > 0 && avail > 0)
      w.style.fontSize = `${(avail / natural) * 100}px`;
  }, []);

  useLayoutEffect(fit, [fit, text]);

  useEffect(() => {
    let raf = 0;
    let lastWidth = 0;
    // Resizing the word changes this box's height, which would re-trigger the observer
    // ("ResizeObserver loop completed with undelivered notifications"). So only react to
    // width changes, and do the work on the next frame, outside the observer callback.
    const schedule = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(fit);
    };
    const ro =
      "ResizeObserver" in window
        ? new ResizeObserver((entries) => {
            const w = Math.round(entries[0].contentRect.width);
            if (w === lastWidth) return;
            lastWidth = w;
            schedule();
          })
        : null;
    if (ro && box.current) ro.observe(box.current);
    if (!ro) window.addEventListener("resize", schedule);
    if (document.fonts && document.fonts.ready)
      document.fonts.ready.then(schedule);
    return () => {
      cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", schedule);
    };
  }, [fit]);

  return (
    <div className={className} ref={box} aria-hidden="true">
      <span ref={word} className="footer__wordText">
        {[...text].map((ch, i) => {
          const pair = i > 0 ? text[i - 1] + ch : "";
          return KERN[pair] ? (
            <span key={i} style={{ marginLeft: `${KERN[pair]}em` }}>
              {ch}
            </span>
          ) : (
            ch
          );
        })}
      </span>
    </div>
  );
};

export default FitWord;
